"use client";

import { Button } from "@/components/ui/button";
import { useErrorToast } from "@/hooks/useErrorToast";
import { parseEmail, resolveSignInType } from "@/lib/util/string/email";
import { parsePhoneNumber } from "@/lib/util/string/phone";
import { setUser as setUserSentry } from "@sentry/nextjs";
import { createClientComponentClient } from "@supabase/auth-helpers-nextjs";
import { AuthError } from "@supabase/supabase-js";
import Image from "next/image";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { usePostHog } from "posthog-js/react";
import React, { useState } from "react";

const LoginForm: React.FC = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const { showErrorToast } = useErrorToast();
  const router = useRouter();
  const supabase = createClientComponentClient();
  const searchParams = useSearchParams();
  const redirectTo = searchParams.get("redirectTo");
  const posthog = usePostHog();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const signInType = resolveSignInType(email);

    if (signInType === "email") {
      await signInWithEmail();
    } else {
      await signInWithPhone();
    }
  };

  const signInWithPhone = async () => {
    const phone = parsePhoneNumber(email);
    if (!phone) {
      showErrorToast({ error: new Error("Invalid Phone Number Format") });
      return;
    }

    const { data: authData, error } = await supabase.auth.signInWithPassword({
      phone: phone,
      password: password,
    });

    await signInCallback(authData, error);
  };

  const signInWithEmail = async () => {
    const parsedEmail = parseEmail(email);
    if (!parsedEmail) {
      showErrorToast({ error: new Error("Invalid Email Id") });
      return;
    }

    const { data: authData, error } = await supabase.auth.signInWithPassword({
      email: parsedEmail,
      password: password,
    });

    await signInCallback(authData, error);
  };

  const signInCallback = async (authData: any, error: AuthError | null) => {
    if (!error) {
      const { data, error } = await supabase
        .from("profile")
        .select("id, email, first_name, last_name, phone, status, account_type")
        .eq("id", authData.user.id)
        .single();

      if (error) {
        await supabase.auth.signOut();
        return;
      }

      if (data.status === "active") {
        const { id, email, phone, first_name, last_name, account_type } = data;
        const { data: hiveUsers } = await supabase
          .from("hive_user")
          .select("hive_id")
          .eq("user_id", id);

        const hiveIds =
          hiveUsers?.map((hu: { hive_id: string }) => hu.hive_id) || [];

        setUserSentry({
          id,
          email,
          phone,
          first_name,
          last_name,
        });

        posthog.identify(id, {
          email,
          phone,
          account_type,
          associated_hive_ids: hiveIds,
        });
        if (redirectTo) {
          router.push(redirectTo);
        } else {
          router.refresh();
        }
        return;
      }

      if (data.status === "deactivated") {
        await supabase.auth.signOut();
        router.push(redirectTo || "/v2/auth/sign-in/inactive-account");
      }
      router.refresh();
    }
    if (error) {
      showErrorToast({ error });
      setEmail("");
      setPassword("");
    }
  };

  return (
    <div className="max-w-[630px] mx-auto mt-14 rounded-2xl sm:shadow-lg p-12 text-center bg-white">
      {/* Logo */}
      <div className="mb-8 flex justify-center">
        <Image
          width={160}
          height={64}
          src={"/images/bb_logo.svg"}
          alt="Brightbee Logo"
        />
      </div>
      <h1 className="text-3xl font-bold mb-2 text-slate-800">
        Login to Account
      </h1>
      <p className="text-sm text-muted-foreground mb-8">
        Please enter your email and password to continue
      </p>
      <form onSubmit={handleSubmit}>
        <div className="text-left mb-5">
          <label className="text-slate-600">
            Phone or Email Address <span className="text-primary">*</span>
          </label>
          <input
            type="text"
            name="username"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="555-125-4567 or your@email.com"
            className="w-full px-4 py-3 rounded-lg border border-slate-200 mt-2 bg-slate-50 focus:outline-none focus:ring-2 focus:ring-yellow-300 !text-sm"
            required
          />
        </div>
        <div className="text-left mb-5">
          <label className="text-slate-600">
            Password <span className="text-primary">*</span>
          </label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            className="w-full px-4 py-3 rounded-lg border border-slate-200 mt-2 bg-slate-50 focus:outline-none focus:ring-2 focus:ring-yellow-300 !text-sm"
            required
          />
        </div>
        <div className="flex items-end justify-between mb-8">
          <Link
            href="/v2/auth/forgot-password"
            className="text-sm text-slate-700 font-medium hover:underline"
          >
            Forgot Password?
          </Link>
        </div>
        <Button className="min-w-[150px]" type="submit">
          Sign In
        </Button>
      </form>
    </div>
  );
};

export default LoginForm;
